import { useState, useEffect } from 'react'
import AnimatedNumber from './AnimatedNumber'
import GlowingTrackBar from './GlowingTrackBar'
import { safeArray, safeEntries } from '../utils/safe'

// Mock analytics data
const ANALYTICS_DATA = {
  projectHealth: {
    labor: { score: 85, trend: 'up', velocity: 'high', risks: 2 },
    rafiq: { score: 72, trend: 'stable', velocity: 'medium', risks: 3 },
    muwafaq: { score: 100, trend: 'up', velocity: 'high', risks: 1 },
    sahha: { score: 80, trend: 'up', velocity: 'medium', risks: 2 },
    dashboard: { score: 100, trend: 'stable', velocity: 'low', risks: 0 },
  },
  developmentVelocity: {
    dailyCommits: 24,
    weeklyCommits: 168,
    monthlyCommits: 720,
    avgCommitSize: '3.2K lines',
    activeBranches: 8,
  },
  costTracking: {
    monthlyTokens: 1185000,
    dailyTokens: 39500,
    costPerToken: 0.000002, // $0.000002 per token
    monthlyCostSAR: 0, // SAR 0 build cost
    monthlyCostUSD: 0, // $0 build cost
    savingsVsHuman: 150000, // SAR 150,000/month savings
  },
  riskIndicators: [
    { id: 1, title: 'Google Maps API Key Missing', project: 'rafiq', severity: 'high', impact: 'Blocks deployment', status: 'blocked' },
    { id: 2, title: 'Labor Portal Web Admin Pending', project: 'labor', severity: 'medium', impact: 'Delays launch', status: 'pending' },
    { id: 3, title: 'Sahha Clinic Reports Due', project: 'sahha', severity: 'medium', impact: 'Feature incomplete', status: 'in_progress' },
    { id: 4, title: 'Muwafaq Evidence Collection', project: 'muwafaq', severity: 'low', impact: 'Compliance gaps', status: 'in_progress' },
  ],
  resourceAllocation: {
    agents: {
      total: 7,
      active: 5,
      idle: 2,
      byProject: {
        labor: 3,
        rafiq: 2,
        muwafaq: 3,
        sahha: 2,
        dashboard: 2,
      }
    },
    tokens: {
      labor: 375000,
      rafiq: 294000,
      muwafaq: 225000,
      sahha: 195000,
      dashboard: 96000,
      infra: 0,
    }
  }
}

// Mock API function
const fetchAnalyticsData = async () => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(ANALYTICS_DATA)
    }, 500)
  })
}

export default function EnhancedAnalytics() {
  const [data, setData] = useState(ANALYTICS_DATA)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState('monthly')

  useEffect(() => {
    const loadData = async () => {
      try {
        const analyticsData = await fetchAnalyticsData()
        setData(analyticsData || ANALYTICS_DATA)
      } catch (error) {
        console.error('Failed to load analytics data:', error)
        setData(ANALYTICS_DATA) // fallback to prevent null crash
      } finally {
        setLoading(false)
      }
    }

    loadData()
    const interval = setInterval(loadData, 30000) // Update every 30 seconds
    return () => clearInterval(interval)
  }, [])

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  }

  const getHealthColor = (score) => {
    if (score >= 90) return '#22C55E'
    if (score >= 70) return '#F59E0B'
    return '#EF4444'
  }

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return '📈'
      case 'down': return '📉'
      case 'stable': return '📊'
      default: return '📊'
    }
  }

  const getVelocityColor = (velocity) => {
    switch (velocity) {
      case 'high': return '#22C55E'
      case 'medium': return '#F59E0B'
      case 'low': return '#EF4444'
      default: return '#64748B'
    }
  }

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return '#EF4444'
      case 'medium': return '#F59E0B'
      case 'low': return '#3B82F6'
      default: return '#64748B'
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-white/[0.04] rounded-lg w-48 animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-64 bg-white/[0.04] rounded-[14px] animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">📈 Enhanced Analytics</h2>
          <p className="text-sm text-[#94A3B8]">Project health, development velocity, cost tracking & risk indicators</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="bg-base border border-white/[0.06] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#8B5CF6]"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>

      {/* Project Health Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Health Scores */}
        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <h3 className="text-lg font-bold text-white mb-4">🏥 Project Health Scores</h3>
          <div className="space-y-4">
            {Object.entries(data?.projectHealth || {}).map(([project, metrics]) => (
              <div key={project} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">
                      {project === 'labor' ? '👷' : 
                       project === 'rafiq' ? '🚗' : 
                       project === 'muwafaq' ? '📋' : 
                       project === 'sahha' ? '🏥' : '📊'}
                    </span>
                    <span className="text-sm font-medium text-white capitalize">{project}</span>
                    <span className="text-xs text-[#64748B]">{getTrendIcon(metrics.trend)}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span 
                      className="text-sm font-bold"
                      style={{ color: getHealthColor(metrics.score) }}
                    >
                      {metrics.score}%
                    </span>
                    <span 
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: `${getVelocityColor(metrics.velocity)}20`,
                        color: getVelocityColor(metrics.velocity),
                        border: `1px solid ${getVelocityColor(metrics.velocity)}40`
                      }}
                    >
                      {metrics.velocity} velocity
                    </span>
                  </div>
                </div>
                <GlowingTrackBar
                  progress={metrics.score}
                  color={getHealthColor(metrics.score)}
                  lightColor={`${getHealthColor(metrics.score)}80`}
                  height="h-2"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#64748B]">
                    {metrics.risks} risk{metrics.risks !== 1 ? 's' : ''}
                  </span>
                  <span className="text-xs text-[#64748B]">
                    Trend: {metrics.trend}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Development Velocity */}
        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <h3 className="text-lg font-bold text-white mb-4">⚡ Development Velocity</h3>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="rounded-[10px] bg-base border border-white/[0.04] p-4 text-center">
              <div className="text-2xl font-bold text-white mb-1">
                {formatNumber(data.developmentVelocity.dailyCommits)}
              </div>
              <div className="text-xs text-[#94A3B8]">Daily Commits</div>
            </div>
            <div className="rounded-[10px] bg-base border border-white/[0.04] p-4 text-center">
              <div className="text-2xl font-bold text-white mb-1">
                {formatNumber(data.developmentVelocity.weeklyCommits)}
              </div>
              <div className="text-xs text-[#94A3B8]">Weekly Commits</div>
            </div>
            <div className="rounded-[10px] bg-base border border-white/[0.04] p-4 text-center">
              <div className="text-2xl font-bold text-white mb-1">
                {data.developmentVelocity.avgCommitSize}
              </div>
              <div className="text-xs text-[#94A3B8]">Avg Commit Size</div>
            </div>
            <div className="rounded-[10px] bg-base border border-white/[0.04] p-4 text-center">
              <div className="text-2xl font-bold text-white mb-1">
                {data.developmentVelocity.activeBranches}
              </div>
              <div className="text-xs text-[#94A3B8]">Active Branches</div>
            </div>
          </div>
          
          <div className="border-t border-white/[0.06] pt-4">
            <h4 className="text-md font-bold text-white mb-3">📊 Commit Activity</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#94A3B8]">Last 24 hours</span>
                <span className="text-sm font-bold text-white">{data.developmentVelocity.dailyCommits} commits</span>
              </div>
              <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full bg-gradient-to-r from-[#00BFA5] to-[#00E5CC]" 
                  style={{ width: '100%' }} 
                />
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-[#94A3B8]">Last 7 days</span>
                <span className="text-sm font-bold text-white">{data.developmentVelocity.weeklyCommits} commits</span>
              </div>
              <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#C084FC]" 
                  style={{ width: '100%' }} 
                />
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-[#94A3B8]">Last 30 days</span>
                <span className="text-sm font-bold text-white">{data.developmentVelocity.monthlyCommits} commits</span>
              </div>
              <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full bg-gradient-to-r from-[#F59E0B] to-[#FCD34D]" 
                  style={{ width: '100%' }} 
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cost Tracking & Risk Indicators */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Tracking */}
        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <h3 className="text-lg font-bold text-white mb-4">💰 Cost Tracking</h3>
          
          {/* SAR 0 Build Cost Highlight */}
          <div className="mb-6 p-4 rounded-[12px] bg-gradient-to-r from-[#22C55E]/20 to-[#86EFAC]/10 border border-[#22C55E]/30">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-white">SAR 0</div>
                <div className="text-sm text-[#94A3B8]">Monthly Build Cost</div>
              </div>
              <div className="text-4xl">🎯</div>
            </div>
            <p className="text-xs text-[#64748B] mt-2">
              AI-agent-only development = Zero human salary cost
            </p>
          </div>
          
          {/* Token Usage */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#94A3B8]">Monthly Token Usage</span>
              <span className="text-lg font-bold text-amber-400">
                {formatNumber(data.costTracking.monthlyTokens)} tokens
              </span>
            </div>
            <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full bg-gradient-to-r from-[#F59E0B] to-[#FCD34D]" 
                style={{ width: '100%' }} 
              />
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#94A3B8]">Daily Token Usage</span>
              <span className="text-lg font-bold text-amber-400">
                {formatNumber(data.costTracking.dailyTokens)} tokens
              </span>
            </div>
            <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#C084FC]" 
                style={{ width: '100%' }} 
              />
            </div>
            
            {/* Cost Breakdown */}
            <div className="border-t border-white/[0.06] pt-4">
              <h4 className="text-md font-bold text-white mb-3">Cost Breakdown</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#94A3B8]">Cost per Token</span>
                  <span className="text-sm font-bold text-white">
                    ${data.costTracking.costPerToken.toFixed(6)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#94A3B8]">Monthly Cost (USD)</span>
                  <span className="text-sm font-bold text-white">
                    ${data.costTracking.monthlyCostUSD.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#94A3B8]">Savings vs Human Team</span>
                  <span className="text-sm font-bold text-green-400">
                    SAR {formatNumber(data.costTracking.savingsVsHuman)}/month
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Risk Indicators */}
        <div className="rounded-[14px] bg-surface border border-white/[0.06] p-5">
          <h3 className="text-lg font-bold text-white mb-4">⚠️ Risk Indicators</h3>
          <div className="space-y-3">
            {(data.riskIndicators || []).map((risk) => (
              <div 
                key={risk.id} 
                className="rounded-[10px] bg-base border border-white/[0.04] p-4 hover:border-white/[0.08] transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-bold text-white">{risk.title}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-white/[0.04] text-[#94A3B8]">
                        {risk.project}
                      </span>
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{
                          backgroundColor: `${getSeverityColor(risk.severity)}20`,
                          color: getSeverityColor(risk.severity),
                          border: `1px solid ${getSeverityColor(risk.severity)}40`
                        }}
                      >
                        {risk.severity} severity
                      </span>
                    </div>
                  </div>
                  <span 
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{
                      backgroundColor: risk.status === 'blocked' ? '#EF444420' : 
                                     risk.status === 'in_progress' ? '#F59E0B20' : '#64748B20',
                      color: risk.status === 'blocked' ? '#EF4444' : 
                             risk.status === 'in_progress' ? '#F59E0B' : '#64748B',
                      border: `1px solid ${risk.status === 'blocked' ? '#EF444440' : 
                                 risk.status === 'in_progress' ? '#F59E0B40' : '#64748B40'}`
                    }}
                  >
                    {risk.status.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-xs text-[#94A3B8]">Impact: {risk.impact}</p>
              </div>
            ))}
          </div>
          
          {/* Resource Allocation */}
          <div className="mt-6 pt-6 border-t border-white/[0.06]">
            <h4 className="text-md font-bold text-white mb-3">🤖 Resource Allocation</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-1">
                  {data?.resourceAllocation?.agents?.active ?? 0}/{data?.resourceAllocation?.agents?.total ?? 0}
                </div>
                <div className="text-xs text-[#94A3B8]">Agents Active</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-400 mb-1">
                  {formatNumber(Object.values(data?.resourceAllocation?.tokens || {}).reduce((a, b) => a + b, 0))}
                </div>
                <div className="text-xs text-[#94A3B8]">Monthly Tokens</div>
              </div>
            </div>
            
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#94A3B8]">Token Distribution</span>
                <span className="text-xs text-[#64748B]">By Project</span>
              </div>
              {Object.entries(data?.resourceAllocation?.tokens || {}).map(([project, tokens]) => (
                <div key={project} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white capitalize">{project}</span>
                    <span className="text-xs text-[#64748B]">{formatNumber(tokens)}</span>
                  </div>
                  <div className="w-full h-1 bg-white/[0.04] rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full"
                      style={{
                        width: `${(tokens / Math.max(Object.values(data?.resourceAllocation?.tokens || {}).reduce((a, b) => a + b, 0), 1)) * 100}%`,
                        backgroundColor: project === 'labor' ? '#FF6B35' :
                                        project === 'rafiq' ? '#00BFA5' :
                                        project === 'muwafaq' ? '#4CAF50' :
                                        project === 'sahha' ? '#448AFF' :
                                        project === 'dashboard' ? '#8B5CF6' : '#64748B'
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}